export type ScreenRotation = 0 | 90 | 180 | 270;
export type RotationDirection = 'clockwise' | 'counterclockwise';
export type ViewerDisplayMode = 'fit' | 'native';

export interface ViewerLayoutInput {
  readonly mode: ViewerDisplayMode;
  readonly rotation: ScreenRotation;
  readonly zoom: number;
  readonly naturalWidth: number;
  readonly naturalHeight: number;
  readonly viewportWidth: number;
  readonly viewportHeight: number;
}

export interface ViewerLayout {
  readonly videoWidth: number;
  readonly videoHeight: number;
  readonly rotatedWidth: number;
  readonly rotatedHeight: number;
}

export function rotateScreen(
  rotation: ScreenRotation,
  direction: RotationDirection,
): ScreenRotation {
  const delta = direction === 'clockwise' ? 90 : -90;
  return ((rotation + delta + 360) % 360) as ScreenRotation;
}

export function calculateViewerLayout(input: ViewerLayoutInput): ViewerLayout | null {
  const { mode, rotation, naturalWidth, naturalHeight, viewportWidth, viewportHeight } = input;
  if (
    !positiveFinite(naturalWidth) ||
    !positiveFinite(naturalHeight) ||
    !positiveFinite(viewportWidth) ||
    !positiveFinite(viewportHeight)
  ) {
    return null;
  }

  const zoom = Number.isFinite(input.zoom) ? Math.min(4, Math.max(0.5, input.zoom)) : 1;
  const swapsAxes = rotation === 90 || rotation === 270;
  const unscaledRotatedWidth = swapsAxes ? naturalHeight : naturalWidth;
  const unscaledRotatedHeight = swapsAxes ? naturalWidth : naturalHeight;
  const fitScale =
    mode === 'fit'
      ? Math.min(viewportWidth / unscaledRotatedWidth, viewportHeight / unscaledRotatedHeight)
      : 1;
  const scale = fitScale * zoom;
  const videoWidth = naturalWidth * scale;
  const videoHeight = naturalHeight * scale;

  return Object.freeze({
    videoWidth,
    videoHeight,
    rotatedWidth: swapsAxes ? videoHeight : videoWidth,
    rotatedHeight: swapsAxes ? videoWidth : videoHeight,
  });
}

function positiveFinite(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}
